import React from 'react';
import { Link, resolvePath, useLocation } from 'react-router-dom';
import './Tabs.css';

/** Arrow/Home/End roving between the items of a tab strip. Returns the focused index, or -1. */
function rove(e: React.KeyboardEvent, items: HTMLElement[]): number {
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return -1;
    const n = items.length;
    const next = { ArrowRight: (i + 1) % n, ArrowDown: (i + 1) % n, ArrowLeft: (i - 1 + n) % n, ArrowUp: (i - 1 + n) % n,
        Home: 0, End: n - 1 }[e.key];
    if (next === undefined) return -1;
    e.preventDefault();
    items[next].focus();
    return next;
}

// ─── Link tabs (route navigation) ────────────────────────────────────────────

export interface TabLinkItem {
    to: string;
    label: React.ReactNode;
    /** Path must match exactly (no nested routes). Implied when `to` has a query. */
    end?: boolean;
    /** Override URL matching for the whole strip (set it on every item), e.g. when the
     *  default tab is shown with no ?tab= in the URL. */
    active?: boolean;
    /** `to`'s query is the whole query: the page's other params are not merged in
     *  (the hash still is). For callers that compute the full next query themselves. */
    replaceQuery?: boolean;
}

/**
 * Section navigation as real links: the URL is the state (shareable, Back/Forward
 * work), the current tab carries aria-current="page", and each link is an ordinary
 * Tab stop. Query tabs (`?tab=`) match on their own params only and keep the rest
 * of the URL's params and hash.
 */
export function Tabs({ label, items, className = '' }: { label: string; items: TabLinkItem[]; className?: string }) {
    const { pathname, search, hash } = useLocation();
    const explicit = items.some((item) => item.active !== undefined);
    let found = false;
    const tabs = items.map((item) => {
        const to = resolvePath(item.to, pathname);
        const match = explicit ? !!item.active : isCurrent(to.pathname, to.search, item.end, pathname, search);
        const current = match && !found; // exactly one aria-current: first match wins
        found ||= current;
        return { ...item, href: href(to.pathname, to.search, pathname, item.replaceQuery ? '' : search, hash), current };
    });
    return (
        <nav aria-label={label} className={`ui-tabs ${className}`}>
            {tabs.map((t, i) => (
                <Link key={i} to={t.href} className={`ui-tab ${t.current ? 'is-active' : ''}`}
                    aria-current={t.current ? 'page' : undefined}>
                    {t.label}
                </Link>
            ))}
        </nav>
    );
}

/** A query tab on the current page keeps the other URL params (filters, paging) and the hash. */
function href(toPath: string, toSearch: string, pathname: string, search: string, hash: string): string {
    if (!toSearch || trim(toPath) !== trim(pathname)) return toPath + toSearch;
    const params = new URLSearchParams(search);
    for (const [k, v] of new URLSearchParams(toSearch)) params.set(k, v);
    return `${toPath}?${params}${hash}`;
}

const trim = (p: string) => p.replace(/\/+$/, '') || '/';

function isCurrent(toPath: string, toSearch: string, end: boolean | undefined, pathname: string, search: string): boolean {
    const [want, have] = [trim(toPath), trim(pathname)];
    const exact = end || toSearch !== '';
    if (!(have === want || (!exact && have.startsWith(want === '/' ? '/' : `${want}/`)))) return false;
    const current = new URLSearchParams(search);
    for (const [k, v] of new URLSearchParams(toSearch)) if (current.get(k) !== v) return false;
    return true;
}

// ─── Button tabs (in-page panels) ────────────────────────────────────────────

export interface ButtonTabItem {
    value: string;
    label: React.ReactNode;
}

/**
 * WAI-ARIA tabs for switching panels inside one page (not navigation). Arrow keys
 * select and focus (automatic activation). Pair each value with a <TabPanel>.
 */
export function ButtonTabs({ label, idPrefix, items, value, onChange, className = '' }: {
    label: string; idPrefix: string; items: ButtonTabItem[]; value: string; onChange: (value: string) => void; className?: string;
}) {
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const next = rove(e, Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')));
        if (next >= 0) onChange(items[next].value);
    };
    return (
        <div role="tablist" aria-label={label} className={`ui-tabs ${className}`} onKeyDown={onKeyDown}>
            {items.map((item) => {
                const selected = item.value === value;
                return (
                    <button
                        key={item.value}
                        type="button"
                        role="tab"
                        id={`${idPrefix}-tab-${item.value}`}
                        aria-controls={`${idPrefix}-panel-${item.value}`}
                        aria-selected={selected}
                        tabIndex={selected ? 0 : -1}
                        className={`ui-tab ${selected ? 'is-active' : ''}`}
                        onClick={() => onChange(item.value)}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
}

export function TabPanel({ idPrefix, value, children }: { idPrefix: string; value: string; children: React.ReactNode }) {
    return (
        <div role="tabpanel" id={`${idPrefix}-panel-${value}`} aria-labelledby={`${idPrefix}-tab-${value}`} tabIndex={0}
            className="ui-tabpanel">
            {children}
        </div>
    );
}
